import { Send, X } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'

import {
  useCreateComment,
  useUpdateComment,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface CommentFormProps {
  issueId: string
  commentId?: string
  initialContent?: string
  onCancel?: () => void
}

const MAX_COMMENT_LENGTH = 10000

/**
 * 评论表单组件
 * 支持创建和编辑评论
 */
export function CommentForm({ issueId, commentId, initialContent = '', onCancel }: CommentFormProps) {
  const isEditing = !!commentId
  const [content, setContent] = useState(initialContent)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const createMutation = useCreateComment()
  const updateMutation = useUpdateComment()

  // 自动聚焦
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.setSelectionRange(content.length, content.length)
    }
  }, [isEditing, content.length])

  // 自动调整高度
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [content])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedContent = content.trim()
    if (!trimmedContent) {
      return
    }

    if (isEditing && commentId) {
      updateMutation.mutate(
        { id: commentId, data: { content: trimmedContent } },
        {
          onSuccess: () => {
            if (onCancel) {
              onCancel()
            }
          },
        }
      )
    } else {
      createMutation.mutate(
        { issueId, data: { content: trimmedContent } },
        {
          onSuccess: () => {
            setContent('')
          },
        }
      )
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl/Cmd + Enter 提交
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSubmit(e)
    }
    // Escape 取消编辑
    if (e.key === 'Escape' && onCancel) {
      onCancel()
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending
  const error = createMutation.error || updateMutation.error

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <div className={cn('rounded-lg border bg-card', isEditing && 'ring-2 ring-primary')}>
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isEditing ? '编辑评论...' : '添加评论... 使用 @[用户名](用户ID) 提及他人'}
          rows={isEditing ? 3 : 2}
          maxLength={MAX_COMMENT_LENGTH}
          className="w-full resize-none border-0 bg-transparent px-3 py-2 text-sm focus:outline-none"
        />

        <div className="flex items-center justify-between border-t px-3 py-2">
          <div className="text-xs text-muted-foreground">
            {content.length}/{MAX_COMMENT_LENGTH} · Ctrl+Enter 发送
          </div>
          <div className="flex items-center gap-2">
            {isEditing && onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm hover:bg-muted"
              >
                <X className="h-3.5 w-3.5" />
                取消
              </button>
            )}
            <button
              type="submit"
              disabled={!content.trim() || isPending}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" />
              {isPending ? '发送中...' : isEditing ? '保存' : '发送'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="text-sm text-destructive">
          {error.message || '操作失败'}
        </div>
      )}
    </form>
  )
}
